import React, { useState } from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, Search, Plus, UserPlus, MoreVertical, Mail, Shield, Warehouse, Edit2, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
type UserRole = 'Admin' | 'Warehouse Staff' | 'Supplier';
interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: 'Active' | 'Inactive';
  lastLogin: string;
  avatar: string;
}
const initialUsers: User[] = [{
  id: 'USR-001',
  name: 'John Doe',
  email: 'john@das.com',
  role: 'Admin',
  status: 'Active',
  lastLogin: '2 hours ago',
  avatar: 'JD'
}, {
  id: 'USR-002',
  name: 'Ahmed Ali',
  email: 'ahmed@warehouse.com',
  role: 'Warehouse Staff',
  status: 'Active',
  lastLogin: '10 mins ago',
  avatar: 'AA'
}, {
  id: 'USR-003',
  name: 'Sarah Smith',
  email: 'sarah@supplier.com',
  role: 'Supplier',
  status: 'Active',
  lastLogin: '1 day ago',
  avatar: 'SS'
}, {
  id: 'USR-004',
  name: 'Mike Ross',
  email: 'mike@das.com',
  role: 'Warehouse Staff',
  status: 'Inactive',
  lastLogin: '1 week ago',
  avatar: 'MR'
}];
export function UserManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-1290">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-1291">
        <div className="flex items-center gap-4" data-id="element-1292">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1293">
            <ArrowLeft size={24} data-id="element-1294" />
          </button>
          <BilingualText en="User Management" ar="إدارة المستخدمين" className="text-xl font-bold" data-id="element-1295" />
        </div>
        <button className="btn-primary py-2 px-4 text-sm" data-id="element-1296">
          <UserPlus size={18} data-id="element-1297" />
          <span data-id="element-1298">Add User</span>
        </button>
      </header>

      <main className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-id="element-1299">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4" data-id="element-1300">
          <div className="flex-1 relative" data-id="element-1301">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} data-id="element-1302" />
            <input type="text" placeholder="Search users by name, email or role..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#2E3192] focus:border-transparent outline-none transition-all" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1303" />
          </div>
          <div className="flex gap-2" data-id="element-1304">
            <select className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium text-slate-600 outline-none focus:ring-2 focus:ring-[#2E3192]" data-id="element-1305">
              <option data-id="element-1306">All Roles</option>
              <option data-id="element-1307">Admin</option>
              <option data-id="element-1308">Warehouse Staff</option>
              <option data-id="element-1309">Supplier</option>
            </select>
            <select className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm font-medium text-slate-600 outline-none focus:ring-2 focus:ring-[#2E3192]" data-id="element-1310">
              <option data-id="element-1311">All Status</option>
              <option data-id="element-1312">Active</option>
              <option data-id="element-1313">Inactive</option>
            </select>
          </div>
        </div>

        {/* User Table */}
        <div className="card overflow-hidden" data-id="element-1314">
          <div className="overflow-x-auto" data-id="element-1315">
            <table className="w-full text-left" data-id="element-1316">
              <thead className="bg-slate-50 text-slate-400 text-xs uppercase tracking-wider" data-id="element-1317">
                <tr data-id="element-1318">
                  <th className="px-6 py-4 font-bold" data-id="element-1319">User</th>
                  <th className="px-6 py-4 font-bold" data-id="element-1320">Role</th>
                  <th className="px-6 py-4 font-bold" data-id="element-1321">Status</th>
                  <th className="px-6 py-4 font-bold" data-id="element-1322">Last Login</th>
                  <th className="px-6 py-4 font-bold text-right" data-id="element-1323">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1324">
                {initialUsers.map(user => <UserRow key={user.id} user={user} data-id="element-1325" />)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Role Permissions Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1326">
          <RoleCard role="Admin" count={2} description="Full system access, user management, and capacity configuration." icon={Shield} color="text-purple-600" bgColor="bg-purple-50" data-id="element-1327" />
          <RoleCard role="Warehouse Staff" count={12} description="Manage appointments, check-ins, and unloading operations." icon={Warehouse} color="text-blue-600" bgColor="bg-blue-50" data-id="element-1328" />
          <RoleCard role="Supplier" count={45} description="Create shipments and book delivery appointments." icon={UserPlus} color="text-green-600" bgColor="bg-green-50" data-id="element-1329" />
        </div>
      </main>
    </div>;
}
function UserRow({
  user
}: {
  user: User;
}) {
  return <tr className="hover:bg-slate-50 transition-colors group" data-id="element-1330">
      <td className="px-6 py-4" data-id="element-1331">
        <Link to={`/users/${user.id}`} className="flex items-center gap-3" data-id="element-1332">
          <div className="w-10 h-10 rounded-full bg-[#2E3192] text-white flex items-center justify-center font-bold text-sm" data-id="element-1333">
            {user.avatar}
          </div>
          <div data-id="element-1334">
            <div className="font-bold text-slate-700 group-hover:text-[#2E3192] transition-colors" data-id="element-1335">
              {user.name}
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1" data-id="element-1336">
              <Mail size={10} data-id="element-1337" />
              {user.email}
            </div>
          </div>
        </Link>
      </td>
      <td className="px-6 py-4" data-id="element-1338">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${user.role === 'Admin' ? 'bg-purple-100 text-purple-700' : user.role === 'Warehouse Staff' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`} data-id="element-1339">
          {user.role}
        </span>
      </td>
      <td className="px-6 py-4" data-id="element-1340">
        <div className="flex items-center gap-1.5" data-id="element-1341">
          {user.status === 'Active' ? <CheckCircle2 size={14} className="text-green-500" data-id="element-1342" /> : <XCircle size={14} className="text-slate-300" data-id="element-1343" />}
          <span className={`text-sm font-medium ${user.status === 'Active' ? 'text-green-600' : 'text-slate-400'}`} data-id="element-1344">
            {user.status}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-500" data-id="element-1345">{user.lastLogin}</td>
      <td className="px-6 py-4 text-right" data-id="element-1346">
        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1347">
          <button className="p-2 text-slate-400 hover:text-[#2E3192] hover:bg-blue-50 rounded-lg transition-all" data-id="element-1348">
            <Edit2 size={16} data-id="element-1349" />
          </button>
          <button className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" data-id="element-1350">
            <Trash2 size={16} data-id="element-1351" />
          </button>
          <button className="p-2 text-slate-400 hover:text-slate-600 rounded-lg" data-id="element-1352">
            <MoreVertical size={16} data-id="element-1353" />
          </button>
        </div>
      </td>
    </tr>;
}
function RoleCard({
  role,
  count,
  description,
  icon: Icon,
  color,
  bgColor
}: any) {
  return <div className="card p-5 space-y-4" data-id="element-1354">
      <div className="flex items-center justify-between" data-id="element-1355">
        <div className={`p-2 rounded-lg ${bgColor} ${color}`} data-id="element-1356">
          <Icon size={20} data-id="element-1357" />
        </div>
        <span className="text-xs font-bold text-slate-400" data-id="element-1358">{count} Users</span>
      </div>
      <div data-id="element-1359">
        <div className="font-bold text-slate-800" data-id="element-1360">{role}</div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed" data-id="element-1361">
          {description}
        </p>
      </div>
    </div>;
}